import { useMemo, useState } from "react";
import axios from "axios";
import { Delete } from "@mui/icons-material";
import { Grid, IconButton, MenuItem, ListItemSecondaryAction } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { IOption } from "../@types/app";
import { IAssignedToOption } from "../types/calendar";
import { useCalendar } from "../contexts/CalendarContext";
import { logEvent } from "../utils/analyticsLogger";
import { getStatusColor, propertyTypeOptions, stateOptions, statusOptions } from "./CalendarUtils";
import FiltersPresetModal from "./FiltersPresetModal";
import CalendarOrderSearch from "./CalendarOrderSearch";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { useInternalUsers } from "../contexts/InternalUserContext";
import { useUserContext } from "../contexts/UserContext";
import _ from "lodash";
import { InternalUser } from "../types/app";

const formatInternalUserToAssignedToOption = (user: InternalUser): IAssignedToOption => ({
  label: user.fullName || "",
  value: user.selectId || "",
  email: user.email || ""
});

interface Props {
  onGoToDate: (date: string) => void;
  /** Mobile renders the order search as a floating bottom bar instead. */
  hideOrderSearch?: boolean;
}

export default function CalendarFilters({ onGoToDate, hideOrderSearch = false }: Props) {
  const {
    selectedPropertyType,
    setSelectedPropertyType,
    selectedStatuses,
    setSelectedStatuses,
    selectedStates,
    setSelectedStates,
    selectedAccountReps,
    setSelectedAccountReps,
    selectedSalesReps,
    setSelectedSalesReps,
    removeFromSavedFilterList,
    restoreSavedFilters,
    savedFilterList,
    selectedSavedFilterPreset,
    setSelectedSavedFilterPreset,
    clearSelectedPresetFilter
  } = useCalendar();

  const { salesRepOptions, internalUserOptions } = useInternalUsers();
  const { teamMembers } = useUserContext();
  const [isNewPresetModalOpen, setIsNewPresetModalOpen] = useState(false);

  const myTeamMembersAsAssignedToOptions = useMemo(
    () => teamMembers.map(formatInternalUserToAssignedToOption),
    [teamMembers]
  );

  const handleRemoveMyTeam = () => {
    const usersOnMyTeamIds = myTeamMembersAsAssignedToOptions.map((u) => u.value);
    setSelectedAccountReps((selectedReps) =>
      selectedReps.filter((s) => s.value != "myteam" && !usersOnMyTeamIds.includes(s.value))
    );
  };

  const handleClick = ({
    selectedOption,
    selectedOptions,
    setSelectedOptions,
    label = ""
  }: {
    selectedOption: IOption;
    selectedOptions: IOption[];
    setSelectedOptions: (value: React.SetStateAction<any>) => void;
    label?: string;
  }) => {
    clearSelectedPresetFilter();
    if (selectedOptions.find((option) => option.value === selectedOption.value)) {
      setSelectedOptions(selectedOptions.filter((option) => option.value !== selectedOption.value));
      return;
    }
    const updatedSelectedOptions = [...selectedOptions, selectedOption];
    setSelectedOptions(updatedSelectedOptions);
    logEvent("Closing Calendar", "Filter 1", label || "");
  };

  const handleRepClick = async ({ selectedOption }: { selectedOption: IOption }) => {
    const { value } = selectedOption;
    clearSelectedPresetFilter();

    ////If option is found, then we remove it otherwise it is an add
    ////This is being doing very backhanded, because MUI's autocomplete treats the event differently
    ////If one clicked on the chip or checkbox
    if (selectedAccountReps.find((option) => option.value === value)) {
      if (value == "myteam") {
        handleRemoveMyTeam();
        return;
      }

      const updatedFilter = selectedAccountReps.filter((option) => option.value !== value);
      setSelectedAccountReps(updatedFilter);
      logEvent("Closing Calendar", "Filter 1", "Assigned To");
      return;
    }
    if (value == "anyfunders" || value == "noclosersassigned" || value == "nofundersassigned") {
      const updatedRepFilters = [...selectedAccountReps, { ...selectedOption, email: "N/A" }];
      setSelectedAccountReps(updatedRepFilters);
      logEvent("Closing Calendar", "Filter 1", "Assigned To");
      return;
    }
    if (value == "myteam") {
      const updatedAccountReps = _.uniqBy([...selectedAccountReps, ...myTeamMembersAsAssignedToOptions], "value");
      const updatedRepFilters = [...updatedAccountReps, { ...selectedOption, email: "N/A" }];
      setSelectedAccountReps(updatedRepFilters);
      logEvent("Closing Calendar", "Filter 1", "Assigned To");
      return;
    }

    const { data } = await axios.get(`proxy/api/contacts/GetSelectUserEmail?userId=${selectedOption.value}`);
    const updatedRepFilters = [...selectedAccountReps, { ...selectedOption, email: data?.email || "" }];
    setSelectedAccountReps(updatedRepFilters);
    logEvent("Closing Calendar", "Filter 1", "Assigned To");
  };

  const handleSalesRepClick = ({ selectedOption }: { selectedOption: IOption }) => {
    clearSelectedPresetFilter();
    const { value } = selectedOption;
    let updatedSalesRepsFilters = [];
    if (value) {
      updatedSalesRepsFilters = [...selectedSalesReps, selectedOption];
    } else {
      updatedSalesRepsFilters = selectedSalesReps.filter((r) => r.value !== value);
    }
    setSelectedSalesReps(updatedSalesRepsFilters);
    logEvent("Closing Calendar", "Filter 1", "Sales Reps");
  };

  const mobileFilterFieldSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: { xs: "#9AA7B3", md: undefined },
        borderWidth: { xs: 1.5, md: undefined }
      },
      "&:hover fieldset": {
        borderColor: { xs: "#6B7A89", md: undefined }
      },
      "&.Mui-focused fieldset": {
        borderColor: { xs: "#091A7A", md: undefined },
        borderWidth: { xs: 1.5, md: undefined }
      }
    },
    "& .MuiInputLabel-root": {
      color: { xs: "#6B7A89", md: undefined }
    }
  };

  return (
    <>
      {hideOrderSearch && (
        <FiltersPresetModal isOpen={isNewPresetModalOpen} handleClose={() => setIsNewPresetModalOpen(false)} />
      )}
      <Grid container spacing={{ xs: 2.25, md: 1 }}>
        {!hideOrderSearch && (
          <Grid item xs={12}>
            <Grid item xs={2} position="absolute" right={220} width={"241px"} top={-82}>
              <FiltersPresetModal isOpen={isNewPresetModalOpen} handleClose={() => setIsNewPresetModalOpen(false)} />
              <CalendarOrderSearch onGoToDate={onGoToDate} />
            </Grid>
          </Grid>
        )}
      <Grid item xs={12} sm={6} md={2}>
        <FormControl size="small" fullWidth sx={mobileFilterFieldSx}>
          <InputLabel>Saved Filter Presets</InputLabel>
          <Select
            size="small"
            fullWidth
            label="Saved Filter Presets"
            value={""}
            // value={selectedSavedFilterPreset}
            // renderValue={() => selectedSavedFilterPreset}
          >
            <MenuItem
              value=""
              onClick={() => {
                setIsNewPresetModalOpen(true);
              }}
            >
              <em>-Add New Filter Preset-</em>
            </MenuItem>
            {Object.keys(savedFilterList).map((key) => (
              <MenuItem
                key={key}
                value={key}
                onClick={() => {
                  restoreSavedFilters(key);
                  setSelectedSavedFilterPreset(key);
                }}
              >
                <div
                  style={{
                    maxWidth: 200,
                    marginRight: "40px",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {key}
                </div>
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromSavedFilterList(key);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <FormControl size="small" fullWidth sx={mobileFilterFieldSx}>
          <InputLabel>Prop. Type</InputLabel>
          <Select
            value={selectedPropertyType}
            label="Property Type"
            onChange={(event: SelectChangeEvent) => {
              const value = event.target.value;
              clearSelectedPresetFilter();
              setSelectedPropertyType(value);
              logEvent("Closing Calendar", "Filter 1", "Propery Type");
            }}
          >
            <MenuItem value="">
              <em>-All-</em>
            </MenuItem>
            {propertyTypeOptions.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <CustomAutocomplete
          options={stateOptions}
          selectedOptions={selectedStates}
          setSelectedOptions={setSelectedStates}
          id="states"
          label="States"
          onlyShowOptionsOnTyping
          handleSelect={handleClick}
          multiple
          sx={mobileFilterFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <CustomAutocomplete
          options={statusOptions}
          selectedOptions={selectedStatuses}
          setSelectedOptions={setSelectedStatuses}
          label="Status"
          getMenuItemStyles={getStatusColor}
          id="statuses"
          handleSelect={handleClick}
          multiple
          sx={mobileFilterFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <CustomAutocomplete
          options={salesRepOptions}
          selectedOptions={selectedSalesReps}
          setSelectedOptions={setSelectedSalesReps}
          handleSelect={handleSalesRepClick}
          id="sales-reps"
          label="Sales Reps"
          onlyShowOptionsOnTyping
          multiple
          sx={mobileFilterFieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <CustomAutocomplete
          options={internalUserOptions}
          selectedOptions={
            selectedAccountReps
          } /* Selected Account Reps is terrible name for this as it also represents selected funders*/
          setSelectedOptions={setSelectedAccountReps}
          handleSelect={handleRepClick}
          id="account-reps"
          label="Assigned to"
          onlyShowOptionsOnTyping
          defaultOptions={[
            { label: "Any Funders", value: "anyfunders" },
            { label: "No Closer Assigned", value: "noclosersassigned" },
            { label: "No Funder Assigned", value: "nofundersassigned" },
            { label: "My Team", value: "myteam" }
          ]}
          multiple
          handleRemoveMyTeam={handleRemoveMyTeam}
          sx={mobileFilterFieldSx}
        />
      </Grid>
      </Grid>
    </>
  );
}
